import '#lit/components/base/loader/index.js';
import '#lit/components/base/list/index.js';
import '#lit/components/base/list_item/index.js';
import '#lit/components/base/tab/index.js';
import '#lit/components/base/tab_group/index.js';
import '#lit/components/base/tab_panel/index.js';
import '#lit/components/datasets/dataset_label/index.js';
import '../filter_type_icon/index.js';

import type {TemplateResult} from 'lit';
import {html} from 'lit';
import {customElement, state, query} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import styles from './style.scss';
import {DatasetType, datasetTypeLabel} from '#lit/types/datasets.js';
import {property} from 'lit-element';
import type {RequestExecutionHandler} from '#lit/utils/http.js';
import {fetcher, RequestHandler} from '#lit/utils/http.js';
import {RequestMethod} from '#lit/common/constants/index.js';
import type {AudienceBuilderField} from '#lit/types/builders/audience_builder/index.js';
import {ExpressionTypes} from '#lit/types/builders/audience_builder/index.js';
import {audienceBuilder} from '#lit/builders/audience_builder/index.js';
import {LAYER_ROOT_KEY} from '#lit/common/constants/builders/audience_builder.js';
import {
  createExpressionFromType,
  FilterNode,
} from '#lit/lib/builder/audience_builder/nodes/filter.js';
import type {StemNode} from '#lit/lib/builder/audience_builder/nodes/stem.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {styleMap} from 'lit/directives/style-map.js';
import {truncateAndKeepWord} from '#lit/utils/strings.js';
import {repeat} from 'lit/directives/repeat.js';
import {TouchController} from '#lit/controllers/touch_controller.js';
import {classMap} from 'lit/directives/class-map.js';
import type FlyTabGroup from '#lit/components/base/tab_group/index.js';
import type {IClassicBuilderNode} from '#builder';
import {filterTypeTransformer} from '#lit/utils/audience_builder/filters.js';
import {displayApiError} from '#lit/utils/notifications.js';
import {FlyErrorCritical, isAbortError} from '#lit/utils/errors.js';
import {consume} from '@lit-labs/context';
import type {AudienceBuilder} from '#lit/builders/audience_builder/builder.js';
import {AudienceBuilderEventTags} from '#lit/builders/audience_builder/builder.js';
import {AudienceBuilderContext} from '#lit/contexts/audience_builder.js';
import {AbortedRequestError} from '#lit/lib/http_service/errors.js';
import {when} from 'lit/directives/when.js';
import type {
  IChannel,
  IChannelMessage,
  IChannelSubscription,
} from '#utils/channel.js';
import {Channel} from '#utils/channel.js';
import {v4} from 'uuid';
import type {EntryData} from '#lit/contexts/entry_data.js';
import {
  DEFAULT_ENTRY_DATA,
  EntryDataContext,
} from '#lit/contexts/entry_data.js';

export const FILTER_ADDITION_VIEW_ELEMENT_TAG = 'ab-filter-addition-view';

export const EMPTY_FILTER_QUERY = '';
export const STARTING_PAGE_COUNT = 1;
export const DEFAULT_SEARCH_LIMIT = 40;
export const MAX_DESCRIPTION_LENGTH = 60;

export interface Option {
  value: string;
  label: string;
  selected?: boolean;
  disabled?: boolean;
  tags?: string[];
  record: FilterSearchResultItem;
}

const QUEUE_EVENT_FIELD_TAG = 'Queue Event Field';
const FETCH_KEY = 'filterUpdateHandler';
export const BASE_FILTER_SEARCH_URL = (id: number) =>
  `/api/v2/dataset_groups/${id}/field_search`;

export type FilterSearchResultItem = AudienceBuilderField;

/**
 * The payload sent whenever a filter is selected or deselected.
 */
export type FilterSelectionEventDetail = {
  action: 'selected' | 'deselected';
  field: AudienceBuilderField;
};

/**
 * A custom event fired whenever a filter is selected or deselected.
 */
export type FilterSelectionEvent = CustomEvent<FilterSelectionEventDetail>;

export type FilterSearchResponse = {
  message: string;
  more: boolean;
  page: number;
  results: FilterSearchResultItem[];
};

export interface IFilterQueryState {
  query: string;
  requestCount: number;
  requestHandler: RequestHandler;
  page: number;
  more: boolean;
  results: Set<Option['value']>;
  error?: Error;
}

export class FilterQueryState implements IFilterQueryState {
  requestCount = 0;
  page = STARTING_PAGE_COUNT;
  more = true;
  requestHandler: RequestHandler;
  results: Set<Option['value']> = new Set();
  error?: Error;

  constructor(
    private host: FilterAdditionView,
    public query: string,
    private executionFn: RequestExecutionHandler
  ) {
    this.query = query;
    this.executionFn = executionFn;
    this.requestHandler = RequestHandler.create({
      [FETCH_KEY]: this.executionFn,
    });

    this.requestHandler.addHook(() => {
      this.requestCount++;
      this.host.fetching = false;
      this.host.paginating = false;
      this.host.requestUpdate();
    });
  }

  get hasError(): boolean {
    return !!this.error;
  }

  abortCurrentRequest(): void {
    this.requestHandler.abortRequest(FETCH_KEY);
  }

  execute({paginating}: {paginating: boolean} = {paginating: false}): void {
    this.host.fetching = true;

    if (paginating) {
      this.host.paginating = true;
      this.host.requestUpdate();
    }

    this.host.requestUpdate();

    this.requestHandler.executeAllRequests();
  }
}

/**
 * View for adding and searching for filters.
 */
@customElement(FILTER_ADDITION_VIEW_ELEMENT_TAG)
export default class FilterAdditionView extends FlywheelComponent {
  touchController = new TouchController(this, {
    predicate: () => this.active,
    callback: () => this.onQueryUpdate(),
  });

  @consume({context: AudienceBuilderContext, subscribe: true})
  @property({type: Object})
  builder: AudienceBuilder = audienceBuilder;

  @consume({context: EntryDataContext, subscribe: true})
  @property({type: Object, noAccessor: true})
  get entryData() {
    return this._entryData || DEFAULT_ENTRY_DATA;
  }

  set entryData(value: EntryData) {
    this._entryData = value;
  }

  private _entryData = DEFAULT_ENTRY_DATA;

  @property({type: Number})
  datasetGroupId!: number;

  @property()
  query = EMPTY_FILTER_QUERY;

  @property()
  level = LAYER_ROOT_KEY;

  @property({type: Number})
  sectionIndex = 0;

  @property()
  datasetType: DatasetType = DatasetType.USER;

  @property({type: Number})
  datasetId?: number;

  @property({type: Boolean})
  active = false;

  /**
   * If true, the component will disallow
   * creating multiple entries with same filter in the same section.
   */
  @property({type: Boolean})
  singleUseFields = false;

  /**
   * An optional property for limiting the possible field types allowed for
   * filter addition.
   */
  @property({type: Array})
  allowedFieldTypes: ExpressionTypes[] = [];

  /**
   * If this component is marked as "standalone," it means we don't want the
   * component to mutate the audience builder store in anyway (i.e, we
   * don't want to add filters to the builder whenever a filter is
   * selected; we just want to emit a selected event and offload
   * behaviour to a parent component).
   */
  @property({type: Boolean})
  standalone = false;

  @state()
  filterQueryStates: Map<string, FilterQueryState> = new Map();

  @state()
  descriptionVisibilityMap: Map<string, boolean> = new Map();

  @state()
  paginating = false;

  @state()
  limit = DEFAULT_SEARCH_LIMIT;

  /**
   * Options value -> object map.
   */
  @state()
  options: Map<string, Option> = new Map();

  @state()
  fetching = false;

  @query('#filter-view')
  $tabGroup?: FlyTabGroup;

  @state()
  protected channel?: IChannel;

  // A list of options values;
  @state()
  private _selected: Map<Option['value'], number> = new Map();

  @state()
  private nodes: Map<Option['value'], IClassicBuilderNode> = new Map();

  private key = v4();

  static get styles() {
    return [styles];
  }

  /**
   * Gets the state for the current query.
   */
  get currentState(): FilterQueryState | undefined {
    return this.filterQueryStates.get(this.query);
  }

  get datasetObject() {
    return this.builder.datasets.find(d => {
      return d.id === this.datasetId;
    });
  }

  /**
   * Returns a list of all currently selected values.
   */
  get selectedValues(): Option['value'][] {
    return Array.from(this._selected.keys());
  }

  /**
   * Returns a list of selected options.
   */
  get selected(): Option[] {
    return Array.from(this.options.values()).filter(o => {
      return this._selected.has(o.value);
    });
  }

  private setupChannel() {
    const changeSubscription: IChannelSubscription = {
      filter: () => true,
      handler: this.onBuilderChange.bind(this),
    };

    this.channel = new Channel(`filter-addition-view:${this.key}`);
    this.channel.subscribe([changeSubscription]);
    this.builder.addSinkChannel(this.channel);
  }

  private removeChannel() {
    if (!this.channel) return;
    this.builder.removeSinkChannel(this.channel);
  }

  /**
   * Determines whether or not a given field, depending on its type, should show
   * up in the set of options for selection.
   */
  private isAllowedFieldType(fieldType: ExpressionTypes): boolean {
    const allowedFieldTypes = new Set(this.allowedFieldTypes);
    if (!allowedFieldTypes.size) return true;

    return allowedFieldTypes.has(fieldType);
  }

  private dispatchChangeEvent() {
    const event = new CustomEvent('change');
    this.dispatchEvent(event);
  }

  private dispatchSelectedEvent(field: AudienceBuilderField) {
    const event = new CustomEvent<FilterSelectionEventDetail>('selected', {
      detail: {
        action: 'selected',
        field,
      },
    });

    this.dispatchEvent(event);
  }

  private dispatchDeselectedEvent(field: AudienceBuilderField) {
    const event = new CustomEvent<FilterSelectionEventDetail>('selected', {
      detail: {
        action: 'deselected',
        field,
      },
    });

    this.dispatchEvent(event);
  }

  private registerOptionDeselected(recordId: Option['record']['id']): void {
    const numberOfUses = this._selected.get(recordId) ?? 0;
    if (numberOfUses === 1) {
      this._selected.delete(recordId);
    } else {
      this._selected.set(recordId, numberOfUses - 1);
    }
  }

  private registerQueryState(query: string): FilterQueryState {
    const state = new FilterQueryState(
      this,
      query,
      this.fetchHandler.bind(this)
    );

    this.filterQueryStates.set(query, state);
    return state;
  }

  private async buildNodeObject(
    record: FilterSearchResultItem
  ): Promise<FilterNode> {
    const level = await this.builder.getLevel(this.level ?? LAYER_ROOT_KEY);
    const isRealtime = record.label_tag === QUEUE_EVENT_FIELD_TAG;
    const node = FilterNode.create({
      level: this.level ?? LAYER_ROOT_KEY,
      index: level?.length ? level.length : 0,
      computed: record.computed ?? false,
      computedFieldId: record.computedFieldId ?? 0,
      isRealtime,
    });

    const expression = createExpressionFromType(
      record.type,
      record.id,
      record.display_name,
      isRealtime
    );
    node.addExpression(expression);

    return node;
  }

  beforeLoad() {
    super.beforeLoad();
    this.setupChannel();
    if (this.builder.datasetGroup?.id && !this.datasetGroupId) {
      this.datasetGroupId = this.builder.datasetGroup.id;
    }

    if (!this.datasetGroupId) {
      throw Error('Required props not provided.');
    }

    // Initializes empty query state.
    this.registerQueryState(this.query);
  }

  disconnectedCallback() {
    this.removeChannel();
    super.disconnectedCallback();
  }

  attachOptionScrollListener(): void {
    if (!this.$tabGroup?.body) return;
    this.$tabGroup.body.addEventListener(
      'scroll',
      this.onOptionListScroll.bind(this)
    );
  }

  onOptionListScroll(e: Event): void {
    const state = this.filterQueryStates.get(this.query);
    if (!state?.more) return;

    const optionList = e.target as HTMLElement;
    const hasScrollbar = optionList.clientHeight < optionList.scrollHeight;

    // Positioning may be a bit miscalculated on the browser
    // depending on some stuff like zoom, css positioning, css layers etc.
    // We create a small margin from the bottom as a positioning threshold
    // and trigger the new fetch when scroll crosses that line
    const safeMarginFromBottom = 10;
    const isAtBottom =
      optionList.scrollHeight - optionList.scrollTop - safeMarginFromBottom <=
      optionList.clientHeight;

    const shouldPaginate = !hasScrollbar || isAtBottom;

    if (shouldPaginate) this.paginate();
  }

  fieldSearchURL(page: number): string {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(this.limit));
    params.set('query', this.query);
    params.set('dataset_id', String(this.datasetId));
    params.set('dataset_type', this.datasetType);
    params.set('include_system_and_computed_fields', 'true');

    if (audienceBuilder.audience) {
      params.set('audience_id', String(audienceBuilder.audience.id));
    }

    // Use queue event endpoint if entryData.queueEvent.id is present
    const baseUrl = this.entryData.queueEvent?.id
      ? `/api/v2/queue_events/${this.entryData.queueEvent.queue_event_id}/field_search`
      : BASE_FILTER_SEARCH_URL(this.datasetGroupId);

    return `${baseUrl}?${params.toString()}`;
  }

  queueEventFieldSearchURL(queueEventId: number, page: number): string {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(this.limit),
      query: this.query,
      dataset_id: String(this.datasetId),
      dataset_type: this.datasetType,
      include_system_and_computed_fields: 'true',
      ...(audienceBuilder.audience && {
        audience_id: String(audienceBuilder.audience.id),
      }),
    });

    return `/api/v2/queue_events/${queueEventId}/field_search?${params.toString()}`;
  }

  private async checkQueueEventHasMore(
    page: number,
    signal: AbortSignal
  ): Promise<boolean> {
    try {
      const queueEventId = this.entryData.queueEvent?.queue_event_id;
      if (!queueEventId) throw Error('Queue Event Id is required');
      const url = this.queueEventFieldSearchURL(queueEventId, page);
      const response = await fetcher<FilterSearchResponse>(url, {
        signal,
        method: RequestMethod.GET,
      });
      return response.more;
    } catch (error) {
      console.warn('Failed to check queue event pagination:', error);
      return false;
    }
  }

  hasQueryState(query: string): boolean {
    return (
      !!this.filterQueryStates.has(query) &&
      !!this.filterQueryStates.get(query)?.requestCount
    );
  }

  async fetchHandler(signal: AbortSignal): Promise<void> {
    try {
      let queryState = this.filterQueryStates.get(this.query);
      if (!queryState) {
        queryState = this.registerQueryState(this.query);
      }

      if (!this.datasetId) {
        throw new AbortedRequestError('Invalid datasetId');
      }

      this.fetching = true;

      // Fetch from dataset group endpoint
      const datasetGroupUrl = this.fieldSearchURL(queryState.page);
      const datasetGroupResponse = await fetcher<FilterSearchResponse>(
        datasetGroupUrl,
        {
          signal,
          method: RequestMethod.GET,
        }
      );

      let combinedResults = [...datasetGroupResponse.results];

      // If queue event ID exists, also fetch from queue event endpoint
      if (this.entryData.queueEvent?.id) {
        try {
          const queueEventUrl = this.queueEventFieldSearchURL(
            this.entryData.queueEvent.id,
            queryState.page
          );
          const queueEventResponse = await fetcher<FilterSearchResponse>(
            queueEventUrl,
            {
              signal,
              method: RequestMethod.GET,
            }
          );

          // Combine results and deduplicate by field ID
          const existingIds = new Set(combinedResults.map(r => r.id));
          const uniqueQueueEventResults = queueEventResponse.results.filter(
            r => !existingIds.has(r.id)
          );
          combinedResults = [...combinedResults, ...uniqueQueueEventResults];
        } catch (queueEventError) {
          // If queue event fetch fails, continue with just dataset group results
          console.warn('Failed to fetch queue event fields:', queueEventError);
        }
      }

      if (queryState.page !== datasetGroupResponse.page) {
        throw Error('Server and client page mismatch!');
      }

      queryState.page += 1;

      const options: Option[] = combinedResults
        .map(filterTypeTransformer.bind(this))
        .filter(result => this.isAllowedFieldType(result.type))
        .map(result => {
          const option = {
            value: result.id,
            label: result.display_name || result.name,
            selected: this._selected.has(result.id),
            record: result,
          };

          if (!this.options.has(option.value)) {
            this.options.set(option.value, option);
          }

          return option;
        });

      // Determine if there are more results (if either endpoint has more)
      const hasMoreDatasetGroup = datasetGroupResponse.more;
      const hasMoreQueueEvent = this.entryData.queueEvent?.id
        ? await this.checkQueueEventHasMore(queryState.page, signal)
        : false;

      queryState.more = hasMoreDatasetGroup || hasMoreQueueEvent;
      queryState.results = new Set([
        ...queryState.results,
        ...options.map(o => o.value),
      ]);

      this.descriptionVisibilityMap.clear();
      setTimeout(() => {
        this.dispatchChangeEvent();
      }, 0);
    } catch (err) {
      console.error(err);
      this.fetching = false;
      this.requestUpdate();
      if (isAbortError(err)) return;

      const queryState = this.filterQueryStates.get(this.query);
      const url = queryState
        ? this.fieldSearchURL(queryState.page)
        : 'Invalid query state';

      new FlyErrorCritical(
        `Error fetching fields for: ${this.datasetId}`,
        {
          label: 'request',
          data: `GET: ${url}`,
        },
        {
          label: 'response',
          data: err,
        }
      ).register('Error in field addition view');

      displayApiError(err);
    } finally {
      this.fetching = false;
      this.requestUpdate();
      await this.performUpdate();
    }
  }

  private dispatchCloseEvent(): void {
    const event = new CustomEvent('close');
    this.dispatchEvent(event);
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('query')) this.onQueryUpdate();
  }

  private _clearQueryState() {
    this.filterQueryStates.clear();
  }

  reset() {
    this._clearQueryState();
    this.registerQueryState(this.query);
    this.filterQueryStates.get(this.query)?.execute();
  }

  onQueryUpdate() {
    if (!this.touchController.touched) return;

    if (!this.hasQueryState(this.query)) {
      this.registerQueryState(this.query);
    }

    this.filterQueryStates.get(this.query)?.execute();
  }

  async paginate(): Promise<void> {
    this.paginating = true;
    this.fetching = true;

    this.requestUpdate();
    await this.performUpdate();

    setTimeout(() => {
      this.filterQueryStates.get(this.query)?.execute({paginating: true});
    }, 200);
  }

  onBuilderChange(message: IChannelMessage) {
    message.tags().forEach(tag => {
      switch (tag.key()) {
        case AudienceBuilderEventTags.NODE_DELETED: {
          const builderNode = tag.value() as IClassicBuilderNode;
          if (builderNode.type !== 'filter') return;
          const node = builderNode as FilterNode;
          node.expressions.forEach(expression => {
            if (this._selected.has(expression.field)) {
              this.registerOptionDeselected(expression.field);
            }
          });
          break;
        }
      }
    });
  }

  async onFilterClick({record}: Option): Promise<void> {
    //We want to forgo audience builder graph mutation if the component
    //has been flagged as standalone.
    if (this.standalone) {
      this._selected.has(record.id)
        ? this.dispatchDeselectedEvent(record)
        : this.dispatchSelectedEvent(record);

      return;
    }

    try {
      if (this._selected.has(record.id) && this.singleUseFields) {
        const node = this.nodes.get(record.id);
        if (node) await this.builder.removeNode(node.id);
        this.registerOptionDeselected(record.id);
        return;
      }

      this._selected.set(record.id, (this._selected.get(record.id) ?? 0) + 1);
      const node = await this.buildNodeObject(record);
      if (typeof this.sectionIndex !== 'undefined') {
        node.index = this.sectionIndex;
      }

      if (this.level === LAYER_ROOT_KEY) {
        this.builder.putNeighborNode(node);
      } else {
        const stem = this.builder.getNodeFromCache(this.level) as StemNode;
        if (!stem) throw Error('Stem not in cache...');

        stem.addNode(node);
      }

      this.nodes.set(record.id, node);
      this.dispatchCloseEvent();
    } catch (err) {
      console.error(err);
    }
  }

  onMoreClick(option: Option) {
    if (this.descriptionVisibilityMap.has(option.value)) {
      this.descriptionVisibilityMap.delete(option.value);
      this.requestUpdate();
      return;
    }

    this.descriptionVisibilityMap.set(option.value, true);
    this.requestUpdate();
  }

  renderFilterOption(option: Option): TemplateResult {
    const description = truncateAndKeepWord(
      option.record.description || '',
      MAX_DESCRIPTION_LENGTH
    );
    const showMore = !!(
      description.length < (option.record.description || '').length
    );
    return html`
      <fly-list-item
        ?selected=${option.selected || this._selected.has(option.record.id)}
        ?disabled=${option.disabled}
        @click=${() => {
          this.onFilterClick(option);
        }}
      >
        <div class="filter-view__list-item">
          <div class="filter-view__list-item__type">
            <ab-filter-type-icon
              type=${option.record.label_tag === QUEUE_EVENT_FIELD_TAG
                ? ExpressionTypes.REALTIME
                : option.record.type}
              title=${option.record.type}
            ></ab-filter-type-icon>
          </div>
          <div class="filter-view__list-item__content">
            <div class="filter-view__list-item__content__label">
              ${option.record.computed
                ? option.record.display_name
                : option.label}
            </div>
            <div
              class="filter-view__list-item__content__description"
              style=${styleMap({
                display: option.record.description ? 'block' : 'none',
              })}
            >
              ${this.descriptionVisibilityMap.has(option.value)
                ? option.record.description
                : description}
              ${showMore && !this.descriptionVisibilityMap.has(option.value)
                ? html`
                    <div
                      class="filter-view__list-item__content__description__more"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        this.onMoreClick(option);
                      }}
                    >
                      ...
                    </div>
                  `
                : ''}
            </div>
          </div>
          ${when(
            option.record.computed,
            () =>
              html` <div class="filter-view__list-item__computed">
                <fly-icon icon="dashboard"></fly-icon>
              </div>`
          )}
        </div>
      </fly-list-item>
    `;
  }

  renderFetchingLoader(): TemplateResult {
    return html`
      <div
        class="filter-view__list__header__loader ${classMap({
          'filter-view__list__header__loader--loading': this.fetching,
          'filter-view__list__header__loader--stopped': !this.fetching,
        })}"
      >
        <fly-loader ?loading=${true}></fly-loader>
      </div>
    `;
  }

  renderFilters(): TemplateResult {
    const queryState = this.filterQueryStates.get(this.query);
    if (!queryState) return html``;

    const options = Array.from(queryState.results)
      .filter(r => this.options.has(r))
      .map(r => this.options.get(r)) as Option[];

    return html`
      <div class="filter-view__list">
        <div class="filter-view__list__header">
          <dataset-label
            type=${this.datasetType}
            name=${this.datasetObject?.name || 'Filters'}
            description=${ifDefined(this.datasetObject?.description)}
          ></dataset-label>
          <slot name="action"></slot>
          ${this.renderFetchingLoader()}
        </div>
        <div class="filter-view__list__contents">
          ${this.query && options.length === 0
            ? html`
                <div class="filter-view__list__contents__empty">
                  There are no filters matching your query.
                </div>
              `
            : ''}
          <fly-list>
            ${repeat(
              options,
              o => o.value,
              o => this.renderFilterOption(o)
            )}
          </fly-list>
          <div
            style=${styleMap({
              height: '12px',
              margin: '12px',
              visibility: this.paginating ? 'visible' : 'hidden',
              display:
                (this.fetching && !this.paginating) ||
                (!this.fetching &&
                  !this.paginating &&
                  !this.currentState?.more) ||
                (this.query && options.length === 0 && !this.paginating)
                  ? 'none'
                  : 'initial',
            })}
          >
            <fly-loader
              ?loading=${true}
              style="--fly-loader-size: 12px; --fly-loader-margin: 0 auto;"
            ></fly-loader>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="filter-view">
        <slot></slot>
        <fly-tab-group
          id="filter-view"
          style="--fly-tab-group-max-width: 400px"
          @rendered=${() => {
            this.attachOptionScrollListener();
          }}
        >
          <fly-tab active slot="nav" panel="filters-panel" id="filter-nav">
            ${datasetTypeLabel.get(this.datasetType)}
          </fly-tab>
          <fly-tab-panel active name="filters-panel">
            ${this.renderFilters()}
          </fly-tab-panel>
        </fly-tab-group>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [FILTER_ADDITION_VIEW_ELEMENT_TAG]: FilterAdditionView;
  }
}
